import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

// The element a drawer wants focus on when it opens — its close button.
const AUTOFOCUS_SELECTOR = '[data-autofocus]';

// The app's mount node. Drawers portal to <body>, so this is the whole
// background: everything that must go unreachable while a drawer is open.
const APP_ROOT_ID = 'root';

const getFocusable = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('inert') && element.getAttribute('aria-hidden') !== 'true'
  );

/**
 * Is another layer open beside the drawer? Everything that renders over the page
 * portals to <body>: the drawer, the discard-changes modal, dropdown panels. The
 * background lives in #root, and a closed drawer is `inert`, so any *other*
 * <body> child with something focusable in it is a live layer of its own.
 *
 * While one is up it owns the keyboard, and trapping Tab inside the drawer would
 * fence focus away from the very thing the user is being asked to answer.
 */
const hasLayerBesideDrawer = (drawer: HTMLElement): boolean =>
  Array.from(document.body.children).some(
    (child) =>
      child instanceof HTMLElement &&
      child !== drawer &&
      child.id !== APP_ROOT_ID &&
      !child.hasAttribute('inert') &&
      getFocusable(child).length > 0
  );

/**
 * Modal-dialog behaviour for a drawer: focus moves in when it opens, Tab is
 * trapped inside it, the background is taken out of the tab order and the
 * accessibility tree, and focus returns to the opener when it closes.
 *
 * `drawerRef` must point at the drawer's own container (the `<aside>`); the
 * container stays mounted while closed, so everything here keys off `isOpen`.
 */
export function useDrawerDialog(isOpen: boolean, drawerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!isOpen) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    // Read before the drawer takes focus, so closing can hand it back.
    const activeOnOpen = document.activeElement;
    const opener =
      activeOnOpen instanceof HTMLElement && activeOnOpen !== document.body ? activeOnOpen : null;

    const focusTarget =
      drawer.querySelector<HTMLElement>(AUTOFOCUS_SELECTOR) ?? getFocusable(drawer)[0] ?? null;
    // `preventScroll`: the drawer slides in over the page, and scrolling the
    // background to the newly focused node would jerk the view behind it.
    focusTarget?.focus({ preventScroll: true });

    // Absent under jsdom in tests that render the drawer on its own — the trap
    // and focus restore are what those tests are after, so this is a no-op.
    const appRoot = document.getElementById(APP_ROOT_ID);
    appRoot?.setAttribute('inert', '');

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;

      const active = document.activeElement;
      // Only a Tab pressed *inside* the drawer is ours. Anything else focused —
      // the discard-changes modal, a portal'd dropdown panel — sits outside the
      // drawer in the DOM and keeps its own native tab order.
      if (!(active instanceof HTMLElement) || !drawer.contains(active)) return;

      // ...and a layer opened over the drawer keeps its tab order too, even
      // before focus has moved into it.
      if (hasLayerBesideDrawer(drawer)) return;

      // Recomputed per keypress: the drawer slides between a list view and a
      // detail view, so its focusable set changes while it is open.
      const focusables = getFocusable(drawer);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      appRoot?.removeAttribute('inert');
      // The opener can be gone by now — a deleted patient row, a collapsed
      // section — and focusing a detached node would drop focus to <body>.
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [isOpen, drawerRef]);
}
